/* @flow */

import clsx from 'clsx';
import { produce } from 'immer';
import * as React from 'react';
import { connect } from 'react-redux';

import type { ChannelMap } from '../../../libs/netgemLibrary/v8/types/Channel';
import type { NETGEM_API_V8_FEED } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import type { NetgemApiV8WidgetItemClick, TILE_CONFIG_TYPE } from '../../../libs/netgemLibrary/v8/types/WidgetConfig';
import type { CombinedReducers } from '../../../redux/reducers';
import type { Dispatch } from '../../../redux/types/types';

import { getSectionChannels } from '../../../helpers/channel/helper';
import EpgManager from '../../../helpers/epg/epgManager';
import Localizer from '../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import { focusSiblingSection } from '../../../helpers/ui/section/navigation';
import { getTileConfig, getTileTypeClass } from '../../../helpers/ui/section/tile';
import { ignoreIfAborted } from '../../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import getTranslatedText from '../../../libs/netgemLibrary/v8/helpers/Lang';
import { FeedProviderKind } from '../../../libs/netgemLibrary/v8/types/Feed';
import { LocalFeed, type NETGEM_API_V8_SECTION } from '../../../libs/netgemLibrary/v8/types/Section';
import sendV8SectionFeedRequest from '../../../redux/netgemApi/actions/v8/feed';
import Item from '../item/Item';
import { getLocalPath, getValuesFromQueryString } from '../section/helper';
import './Section.css';

type ReduxSectionGridDispatchToPropsType = {
  +localSendV8SectionFeedRequest: (section: NETGEM_API_V8_SECTION, searchString: ?string, signal?: AbortSignal) => Promise<NETGEM_API_V8_FEED>,
};

type ReduxSectionGridReducerStateType = {
  +channels: ChannelMap,
  +gridSectionId: string | null,
  +state: CombinedReducers,
};

type DefaultProps = {
  +innerRef?: ((SectionGridView | null) => unknown) | null,
  +isInExploreModal?: boolean,
  +onItemClick?: NetgemApiV8WidgetItemClick,
  +searchString?: string | null,
};

type SectionGridPropType = {
  ...DefaultProps,
  +section: NETGEM_API_V8_SECTION,
};

type CompleteSectionGridPropType = {
  ...SectionGridPropType,
  ...ReduxSectionGridReducerStateType,
  ...ReduxSectionGridDispatchToPropsType,
};

type SectionGridStateType = {
  feed: NETGEM_API_V8_FEED,
  isCollapsed: boolean,
};

class SectionGridView extends React.PureComponent<CompleteSectionGridPropType, SectionGridStateType> {
  abortController: AbortController;
  gridContainerRef: HTMLElement | null;
  sectionChannels: Set<string> | null;
  sectionName: string;
  tileConfig: TILE_CONFIG_TYPE;

  static defaultProps: DefaultProps = {
    innerRef: null,
    isInExploreModal: false,
    onItemClick: undefined,
    searchString: null,
  };

  constructor(props: CompleteSectionGridPropType) {
    super(props);

    const {
      section,
      section: { id },
    } = props;

    this.abortController = new AbortController();
    this.gridContainerRef = null;
    this.sectionChannels = null;
    this.sectionName = id;
    this.tileConfig = getTileConfig(section);

    this.state = { feed: [], isCollapsed: false };
  }

  componentDidMount() {
    const { innerRef } = this.props;

    if (innerRef) {
      // Expose the class instance
      innerRef(this);
    }

    this.loadData();
  }

  componentDidUpdate(prevProps: CompleteSectionGridPropType) {
    const { gridSectionId } = this.props;
    const { gridSectionId: prevGridSectionId } = prevProps;

    if (gridSectionId !== prevGridSectionId) {
      this.updateIsCollapsed();
    }
  }

  componentWillUnmount() {
    const { innerRef } = this.props;

    if (innerRef) {
      innerRef(null);
    }
  }

  loadData = () => {
    const {
      channels,
      section,
      section: { model },
      state,
    } = this.props;

    let { provider } = model;
    let uri: string = '';
    let path: ?string = null;

    if (provider === FeedProviderKind.Aggregation) {
      const { sources = [] } = model;

      if (!sources || sources.length === 0) {
        return;
      }

      // Pick first model
      [{ provider, uri }] = sources;
    } else {
      ({ uri = '' } = model);
    }

    if (provider === FeedProviderKind.Local) {
      /*
       * Examples:
       *  local://channels/list --> same as ?event=live
       *  local://channels/list?event=live&channels={channelList}
       *  local://channels/list?event=next&channels={channelList}
       *  local://wishlist/?q=scheduledevent
       *  local://wishlist/?q=(catchup|recording|svod|tvod|est)
       *  local://viewinghistory/?q=(catchup|recording)&range=P15D
       */
      path = getLocalPath(uri);
    }

    switch (provider) {
      case FeedProviderKind.Local:
        // Local provider

        switch (path) {
          case LocalFeed.Channels: {
            // Feed of local channels
            const { channels: channelsParam } = getValuesFromQueryString(uri, ['channels']);
            this.sectionChannels = getSectionChannels(section, channelsParam, channels, state);

            const feed = EpgManager.buildFakeChannelsSection(section, this.sectionChannels);
            if (feed !== null) {
              if (feed.length === 0) {
                this.setEmptySectionLoaded();
              } else {
                this.setState({ feed });
              }
            }
            break;
          }

          default:
            Messenger.emit(
              `Type de feed inconnu&nbsp;: ${path ?? ''}`,
              MessengerEvents.NOTIFY_ERROR,
              <div className='toastContent'>
                <div>Type de feed inconnu&nbsp;:</div>
                <div>
                  <b>{path}</b>
                </div>
              </div>,
              undefined,
            );
            break;
        }
        break;

      case FeedProviderKind.NtgVideo:
      default:
        // Regular sections (replay, future, live)
        this.buildRegularSection();
    }
  };

  updateIsCollapsed: () => void = () => {
    const {
      gridSectionId,
      section: { id },
    } = this.props;

    this.setState({ isCollapsed: gridSectionId !== null && id !== gridSectionId });
  };

  buildRegularSection: () => void = () => {
    const { localSendV8SectionFeedRequest, searchString, section } = this.props;
    const {
      abortController: { signal },
    } = this;

    localSendV8SectionFeedRequest(section, searchString, signal)
      .then((feed: NETGEM_API_V8_FEED) => {
        signal.throwIfAborted();

        this.setSectionLoaded(feed);
      })
      .catch((error) => ignoreIfAborted(signal, error, this.setEmptySectionLoaded));
  };

  setEmptySectionLoaded: () => void = () => {
    this.setSectionLoaded([]);
  };

  setSectionLoaded: (feed: NETGEM_API_V8_FEED) => void = (feed) => {
    this.setState(
      produce((draft) => {
        draft.feed = feed;
      }),
    );
  };

  calculateColumnCount = (): number => {
    const { gridContainerRef: grid } = this;

    if (!grid || grid.children.length === 0) {
      return 1;
    }

    const firstItem = grid.children[0];
    if (!(firstItem instanceof HTMLElement)) {
      return 1;
    }

    const gridWidth = grid.clientWidth;
    const itemWidth = firstItem.offsetWidth;

    // Estimate columns per row based on container and element width
    return Math.max(1, Math.floor(gridWidth / itemWidth));
  };

  handleGridKeyDown = (event: SyntheticKeyboardEvent<HTMLElement>): void => {
    const { gridContainerRef: grid } = this;
    const { key } = event;

    if (!grid) {
      return;
    }

    const focusableItems = Array.from(grid.querySelectorAll<HTMLElement>('.sectionItem, .tileContainer[tabindex="0"]'));
    const totalItems = focusableItems.length;

    if (totalItems === 0) {
      return;
    }

    const activeElement = document.activeElement;
    const currentIndex = focusableItems.findIndex((item) => item === activeElement || item.contains(activeElement));

    if (currentIndex === -1) {
      return;
    }

    const columnsCount = this.calculateColumnCount();
    let nextIndex = currentIndex;

    switch (key) {
      case 'ArrowLeft':
        nextIndex = Math.max(0, currentIndex - 1);
        break;
      case 'ArrowRight':
        nextIndex = Math.min(totalItems - 1, currentIndex + 1);
        break;
      case 'ArrowUp':
        nextIndex = currentIndex - columnsCount;
        break;
      case 'ArrowDown':
        nextIndex = Math.min(totalItems - 1, currentIndex + columnsCount);
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = totalItems - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (nextIndex < 0) {
      // User pressed arrow up while on the first row of tiles
      focusSiblingSection(grid, key);
      return;
    }

    const targetElement = focusableItems[nextIndex];
    if (targetElement instanceof HTMLElement) {
      targetElement.focus();
      targetElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  };

  renderTiles: () => Array<React.Node> = () => {
    const { isInExploreModal, onItemClick } = this.props;
    const { feed } = this.state;
    const { tileConfig } = this;

    if (!feed) {
      return [];
    }

    /* eslint-disable react/no-array-index-key */
    return feed.map((item, i) => <Item isInExploreModal={isInExploreModal} item={item} key={i} onItemClick={onItemClick} tileConfig={tileConfig} />);
    /* eslint-enable react/no-array-index-key */
  };

  render(): React.Node {
    const { section } = this.props;
    const { feed, isCollapsed } = this.state;
    const {
      tileConfig: { imageLayout, type },
    } = this;

    if (feed.length === 0 || isCollapsed) {
      // Do not display empty section or collapsed one
      return null;
    }

    // Tile type to CSS class conversion (e.g. 'gemtv.big' -> 'gemtv big')
    const tileTypeClass = getTileTypeClass(type);

    const sectionTitle = getTranslatedText(section.title, Localizer.language);

    return (
      <div className={clsx('section', 'channelSection', tileTypeClass, imageLayout)} id={`sectionGrid_${this.sectionName}`}>
        <div className='sectionGridHeader'>{sectionTitle}</div>
        <div
          aria-label={sectionTitle}
          className='itemGrid'
          onKeyDown={this.handleGridKeyDown}
          ref={(instance: HTMLElement | null) => {
            this.gridContainerRef = instance;
          }}
          role='grid'
        >
          {this.renderTiles()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: CombinedReducers): ReduxSectionGridReducerStateType => {
  return {
    channels: state.appConfiguration.deviceChannels,
    gridSectionId: state.ui.gridSectionId,
    state,
  };
};

const mapDispatchToProps: (dispatch: Dispatch) => ReduxSectionGridDispatchToPropsType = (dispatch) => {
  return {
    localSendV8SectionFeedRequest: (section: NETGEM_API_V8_SECTION, searchString: ?string, signal?: AbortSignal): Promise<NETGEM_API_V8_FEED> =>
      dispatch(sendV8SectionFeedRequest(section, searchString, signal)),
  };
};

const SectionGrid: React.ComponentType<SectionGridPropType> = connect(mapStateToProps, mapDispatchToProps)(SectionGridView);

export { SectionGridView };
export default SectionGrid;
